/**
 * Shared Tailwind class vocabulary (docs/Decisions.md D41, blueprint D-13 as
 * amended 2026-10-19). These replace the retired `.btn-*`, `.card`,
 * `.field-*`, `.pill-*`, `.alert-*` and `.table` stylesheet rules with
 * utility classes themed by the dcon design system. Every colour, radius and
 * shadow is a design-system utility (`dcon-*` or a zone utility `z-*`); no
 * Tailwind palette class, hex literal or `dark:` variant belongs here.
 *
 * Usage: `className={ui.btnPrimary}` or `className={cn(ui.card, 'mt-4')}`.
 */

/** Join class names, dropping falsy values. */
export function cn(...parts) {
  return parts.flat().filter(Boolean).join(' ');
}

// No border colour in the base: a same-property utility in a variant would
// otherwise lose to it (Tailwind orders same-property utilities by value).
const btnBase =
  'inline-flex min-h-z-ctl cursor-pointer items-center justify-center gap-2 rounded-z-ctl border px-4 py-2 font-display text-label no-underline transition-colors hover:no-underline disabled:cursor-not-allowed disabled:opacity-55';

const pillBase = 'inline-flex items-center rounded-z-tag px-3 py-0.5 text-caption font-semibold';

const alertBase = 'mb-4 rounded-dcon-md px-4 py-3 text-body-s';

export const ui = {
  // Layout
  container: 'mx-auto w-full max-w-content px-4',
  page: 'pt-6 pb-16',
  row: 'flex flex-wrap items-center gap-3 *:min-w-0',
  spread: 'justify-between',
  grid: 'grid gap-4 *:min-w-0',
  gridMd2: 'md:grid-cols-2',
  gridMd3: 'md:grid-cols-3',
  muted: 'text-dcon-ink-muted',
  subtle: 'text-dcon-ink-subtle',

  // Surfaces
  card: 'rounded-z-card border border-z-line bg-z-surface p-6 shadow-dcon-md',
  cardFlat: 'rounded-z-card border border-z-line bg-z-surface p-6',
  cardClickable:
    'cursor-pointer rounded-z-card border border-z-line bg-z-surface p-6 shadow-dcon-md hover:border-dcon-logo-blue focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dcon-focus',

  // Forms
  field: 'mb-4 block',
  label: 'mb-1 block text-body-s font-semibold text-dcon-ink-body',
  input:
    'w-full rounded-dcon-md border border-dcon-line-strong bg-z-surface px-3 py-2 text-body text-dcon-ink placeholder:text-dcon-ink-subtle focus:outline-2 focus:outline-offset-1 focus:outline-dcon-focus disabled:opacity-60',
  hint: 'mt-1 text-caption text-dcon-ink-subtle',
  // A field the API or the form refused: red border plus the message under it.
  inputInvalid: 'border-2! border-dcon-danger!',
  fieldError: 'mt-1 block text-caption font-semibold text-dcon-danger',
  checkbox: 'size-4 accent-dcon-brand',

  // Buttons (pill geometry in the member zone, 8px in the console zone)
  btn: btnBase,
  btnPrimary: `${btnBase} border-transparent bg-z-accent text-z-on-accent shadow-dcon-brand hover:bg-z-accent-strong`,
  btnSecondary: `${btnBase} border-dcon-logo-blue bg-z-surface text-dcon-brand hover:bg-dcon-brand-soft`,
  btnDanger: `${btnBase} border-transparent bg-dcon-danger text-dcon-on-danger hover:opacity-90`,
  btnAccent: `${btnBase} border-transparent bg-dcon-accent text-dcon-on-accent shadow-dcon-accent hover:bg-dcon-accent-strong`,
  btnGhost: `${btnBase} border-transparent bg-transparent text-dcon-brand hover:bg-dcon-brand-soft`,
  // Size modifier for any button key: the important suffix makes it win over
  // the base's min-h, px, py and text size whatever Tailwind's output order.
  btnSm: 'min-h-ctl-sm! px-3! py-1! text-body-s!',

  // Status pills
  pill: pillBase,
  pillSuccess: `${pillBase} bg-dcon-success-soft text-dcon-success`,
  pillWarning: `${pillBase} bg-dcon-warning-soft text-dcon-warning`,
  pillDanger: `${pillBase} bg-dcon-danger-soft text-dcon-danger`,
  pillInfo: `${pillBase} bg-dcon-info-soft text-dcon-info`,
  pillNeutral: `${pillBase} bg-dcon-brand-soft text-dcon-brand`,
  pillPrimary: `${pillBase} bg-z-accent text-z-on-accent`,
  pillPremium: `${pillBase} bg-dcon-accent-soft text-dcon-accent-ink`,
  countBadge: `${pillBase} min-w-6 justify-center bg-dcon-accent text-dcon-on-accent tabular-nums`,
  unreadDot: 'inline-block size-2 shrink-0 rounded-dcon-pill bg-dcon-accent',

  // Alerts
  alertError: `${alertBase} bg-dcon-danger-soft text-dcon-danger`,
  alertSuccess: `${alertBase} bg-dcon-success-soft text-dcon-success`,
  alertInfo: `${alertBase} bg-dcon-info-soft text-dcon-info`,
  alertWarning: `${alertBase} bg-dcon-warning-soft text-dcon-warning`,

  // Tables
  tableWrap: 'max-w-full overflow-x-auto',
  table:
    'w-full border-collapse text-body-s [&_td]:border-b [&_td]:border-z-line [&_td]:px-3 [&_td]:py-2 [&_td]:text-left [&_th]:border-b [&_th]:border-z-line [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-dcon-ink-muted',

  // Tabs
  tabs: 'flex flex-wrap items-center gap-3',

  // Headings (font family and ink come from the base layer)
  h1: 'mb-3 text-title-l',
  h2: 'mb-3 text-title-m',
  h3: 'mb-3 text-title-s',
  eyebrow: 'text-eyebrow uppercase text-dcon-ink-muted',

  // Auth screens (login, register, verify, OAuth callback): centered card on a
  // deep band, no app chrome. Replaces `.auth-screen` / `.auth-card` / `.auth-brand`.
  authScreen: 'grid min-h-screen place-items-center bg-dcon-deep p-4',
  authCard: 'w-full max-w-104 rounded-dcon-lg bg-z-surface p-6 text-dcon-ink-body shadow-dcon-md',
  authBrand: 'mb-5 text-center',

  // Public (signed-out) organization pages. Replace `.public-*`.
  publicPage: 'flex min-h-screen flex-col bg-z-canvas',
  publicContainer: 'mx-auto w-full max-w-5xl px-4',
  // The header's direct child (its publicContainer) becomes a row from md up.
  publicHeader:
    'border-b border-z-line bg-z-surface py-4 md:*:flex md:*:items-center md:*:justify-between',
  publicHeaderActions: 'mt-3 flex gap-2 md:mt-0',
  publicOrgHero: 'flex flex-wrap items-center gap-4',
  publicOrgLogo: 'size-16 rounded-dcon-md object-contain',
  publicEventCard: 'block transition-shadow hover:shadow-dcon-lg',
  publicFooter: 'mt-auto border-t border-z-line py-6 text-center',

  // Link on the deep header (org switcher "Browse" / "Organizations").
  orgBrowseLink:
    'whitespace-nowrap text-caption text-dcon-on-deep-muted no-underline hover:text-dcon-on-deep hover:no-underline',
};

export default ui;
